/**
 * SupabaseService.ts
 * Client Supabase pour l'application
 *
 * PR-04 : Types DB générés - Utilise maintenant les types auto-générés
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_CONFIG } from '../config/supabase.config';
import { logger } from '../utils/ProfessionalLogger';
import type { Database } from '../types/supabase';

/**
 * Client Supabase singleton
 */
let supabaseClient: SupabaseClient<Database> | null = null;

/**
 * Vérifie si la configuration Supabase est disponible
 */
export function isSupabaseConfigured(): boolean {
  return !!(SUPABASE_CONFIG.url && SUPABASE_CONFIG.anonKey);
}

/**
 * Initialise et retourne le client Supabase
 * Retourne null si la configuration n'est pas disponible (mode hors ligne)
 */
export function getSupabaseClient(): SupabaseClient<Database> | null {
  if (!isSupabaseConfigured()) {
    logger.warn(
      "Configuration Supabase manquante. L'application fonctionnera en mode hors ligne",
      {
        message:
          'Vérifiez que VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY sont définis dans votre fichier .env',
      },
      'SupabaseService'
    );
    return null;
  }

  if (!supabaseClient) {
    supabaseClient = createClient<Database>(SUPABASE_CONFIG.url, SUPABASE_CONFIG.anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  }

  return supabaseClient;
}

/**
 * Export du client Supabase (alias pour compatibilité)
 * Retourne null si la configuration n'est pas disponible
 */
export const supabase = getSupabaseClient();

/**
 * Service Supabase (objet avec propriété supabase pour compatibilité)
 */
export const supabaseService = {
  get supabase() {
    return getSupabaseClient();
  },
};
